import { useState, useEffect, useCallback } from 'react';
import { Modal, useModalClose } from '@/components/ui/Modal';
import type { ButtonHTMLAttributes } from 'react';
import {
  Search, Plus, X, Loader2, FileText, Pencil, Trash2, Save,
  CheckCircle, XCircle, Filter, ChevronUp, ChevronDown,
} from 'lucide-react';
import { api } from '@/utils/api';
import { formatDate } from '@/utils/formatters';
import { loadAllPages } from '@/utils/loadAllPages';

// ─── Types ────────────────────────────────────────────────────────────────────

interface TransportContract {
  id: string;
  number: string;
  vendorId: string;
  vendorName?: string;
  vendor?: { id: string; name: string };
  title: string;
  status: 'draft' | 'active' | 'expired' | 'terminated';
  validFrom: string;
  validTo: string;
  paymentTerms?: string;
  notes?: string;
  routes?: ContractRoute[];
  _count?: { routes?: number };
  createdAt?: string;
}

interface ContractRoute {
  origin: string;
  destination: string;
  vehicleType: string;
  maxTonnage?: number;
  ratePerTrip?: number;
  ratePerKg?: number;
}

interface Vendor {
  id: string;
  name: string;
  paymentTerms?: string | null;
}

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  active: 'bg-green-50 text-green-700',
  expired: 'bg-red-50 text-red-700',
  terminated: 'bg-orange-50 text-orange-700',
};

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  active: 'Active',
  expired: 'Expired',
  terminated: 'Terminated',
};

// A saved or vendor-given term that is not in the list still shows (never silently replaced).
const termOptions = (current: string) => (current && !PAYMENT_TERMS.includes(current) ? [...PAYMENT_TERMS, current] : PAYMENT_TERMS);

const PAYMENT_TERMS = ['Net 15', 'Net 30', 'Net 45', 'Net 60', 'Advance', 'COD'];

const VEHICLE_TYPES = [
  'Open Truck', 'Closed Container', 'Trailer', 'Mini Truck',
  'Tanker', 'Flatbed', 'Refrigerated', 'Other',
];

// ─── Main Component ───────────────────────────────────────────────────────────

export function TransportContractsPage() {
  const [contracts, setContracts] = useState<TransportContract[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const [sortField, setSortField] = useState<string>('createdAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  // Pagination
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;
  const totalPages = Math.ceil(total / limit);

  // Create Modal
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    vendorId: '', title: '', validFrom: '', validTo: '',
    paymentTerms: '', notes: '',
  });
  const [routes, setRoutes] = useState<ContractRoute[]>([
    { origin: '', destination: '', vehicleType: 'Open Truck', maxTonnage: undefined, ratePerTrip: undefined, ratePerKg: undefined },
  ]);

  // Edit Modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editForm, setEditForm] = useState({
    id: '', vendorId: '', title: '', validFrom: '', validTo: '',
    paymentTerms: '', notes: '',
  });
  const [editRoutes, setEditRoutes] = useState<ContractRoute[]>([]);

  const fetchContracts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params: Record<string, string> = {};
      if (search) params.search = search;
      if (filterStatus) params.status = filterStatus;
      if (sortField) params.sortBy = sortField;
      if (sortDir) params.sortDir = sortDir;
      params.page = String(page);
      params.limit = String(limit);
      const res = await api.get('/contracts/transport-contracts', { params });
      setContracts(res.data.data?.transportContracts ?? res.data.data ?? []);
      setTotal(res.data.pagination?.total ?? res.data.data?.total ?? res.data.total ?? 0);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load transport contracts');
    } finally {
      setLoading(false);
    }
  }, [search, filterStatus, sortField, sortDir, page]);

  useEffect(() => { fetchContracts(); }, [fetchContracts]);

  // Picker loads every page (SUN-286): a plain /vendors call showed only the first 20.
  useEffect(() => {
    const ctl = new AbortController();
    loadAllPages<Vendor>('/vendors', ctl.signal, { label: 'Vendor' }).then(setVendors).catch(() => {});
    return () => ctl.abort();
  }, []);

  const handleCreate = async () => {
    const validRoutes = routes.filter(r => r.origin.trim() && r.destination.trim());
    if (validRoutes.length === 0) {
      alert('Please add at least one route with origin and destination');
      return;
    }
    try {
      setSaving(true);
      await api.post('/contracts/transport-contracts', {
        vendorId: form.vendorId,
        title: form.title,
        validFrom: form.validFrom,
        validTo: form.validTo,
        paymentTerms: form.paymentTerms || undefined,
        notes: form.notes || undefined,
        routes: validRoutes.map(r => ({
          origin: r.origin,
          destination: r.destination,
          vehicleType: r.vehicleType,
          maxTonnage: r.maxTonnage ? Number(r.maxTonnage) : undefined,
          ratePerTrip: r.ratePerTrip ? Number(r.ratePerTrip) : undefined,
          ratePerKg: r.ratePerKg ? Number(r.ratePerKg) : undefined,
        })),
      });
      setShowModal(false);
      setForm({ vendorId: '', title: '', validFrom: '', validTo: '', paymentTerms: '', notes: '' });
      setRoutes([{ origin: '', destination: '', vehicleType: 'Open Truck', maxTonnage: undefined, ratePerTrip: undefined, ratePerKg: undefined }]);
      fetchContracts();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to create transport contract');
    } finally {
      setSaving(false);
    }
  };

  const openEditModal = (c: TransportContract) => {
    if (c.status !== 'draft') {
      alert('Only draft contracts can be edited');
      return;
    }
    setEditForm({
      id: c.id,
      vendorId: c.vendorId || c.vendor?.id || '',
      title: c.title,
      validFrom: c.validFrom ? c.validFrom.split('T')[0] : '',
      validTo: c.validTo ? c.validTo.split('T')[0] : '',
      paymentTerms: c.paymentTerms || '',
      notes: c.notes || '',
    });
    setEditRoutes(c.routes && c.routes.length > 0
      ? c.routes.map(r => ({ ...r }))
      : [{ origin: '', destination: '', vehicleType: 'Open Truck', maxTonnage: undefined, ratePerTrip: undefined, ratePerKg: undefined }]
    );
    setShowEditModal(true);
  };

  const handleEdit = async () => {
    const validRoutes = editRoutes.filter(r => r.origin.trim() && r.destination.trim());
    try {
      setEditSaving(true);
      await api.put(`/contracts/transport-contracts/${editForm.id}`, {
        vendorId: editForm.vendorId,
        title: editForm.title,
        validFrom: editForm.validFrom,
        validTo: editForm.validTo,
        paymentTerms: editForm.paymentTerms || undefined,
        notes: editForm.notes || undefined,
        routes: validRoutes.map(r => ({
          origin: r.origin,
          destination: r.destination,
          vehicleType: r.vehicleType,
          maxTonnage: r.maxTonnage ? Number(r.maxTonnage) : undefined,
          ratePerTrip: r.ratePerTrip ? Number(r.ratePerTrip) : undefined,
          ratePerKg: r.ratePerKg ? Number(r.ratePerKg) : undefined,
        })),
      });
      setShowEditModal(false);
      fetchContracts();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to update transport contract');
    } finally {
      setEditSaving(false);
    }
  };

  const handleActivate = async (c: TransportContract) => {
    if (c.status !== 'draft') return;
    if (!confirm(`Activate contract "${c.title}"? This will make it active and immutable.`)) return;
    try {
      await api.patch(`/contracts/transport-contracts/${c.id}/activate`);
      fetchContracts();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to activate contract');
    }
  };

  const handleTerminate = async (c: TransportContract) => {
    if (c.status !== 'active') return;
    if (!confirm(`Terminate contract "${c.title}"? This action cannot be undone.`)) return;
    try {
      await api.patch(`/contracts/transport-contracts/${c.id}/terminate`);
      fetchContracts();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to terminate contract');
    }
  };

  const handleDelete = async (c: TransportContract) => {
    if (c.status !== 'draft') {
      alert('Only draft contracts can be deleted');
      return;
    }
    if (!confirm(`Delete contract "${c.title}"?`)) return;
    try {
      await api.delete(`/contracts/transport-contracts/${c.id}`);
      fetchContracts();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to delete contract');
    }
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ field }: { field: string }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
  };

  const addRoute = (target: 'create' | 'edit') => {
    const newRoute: ContractRoute = { origin: '', destination: '', vehicleType: 'Open Truck', maxTonnage: undefined, ratePerTrip: undefined, ratePerKg: undefined };
    if (target === 'create') setRoutes([...routes, newRoute]);
    else setEditRoutes([...editRoutes, newRoute]);
  };

  const removeRoute = (idx: number, target: 'create' | 'edit') => {
    if (target === 'create') {
      if (routes.length <= 1) return;
      setRoutes(routes.filter((_, i) => i !== idx));
    } else {
      if (editRoutes.length <= 1) return;
      setEditRoutes(editRoutes.filter((_, i) => i !== idx));
    }
  };

  const updateRoute = (idx: number, field: keyof ContractRoute, value: any, target: 'create' | 'edit') => {
    if (target === 'create') {
      const updated = [...routes];
      updated[idx] = { ...updated[idx], [field]: value };
      setRoutes(updated);
    } else {
      const updated = [...editRoutes];
      updated[idx] = { ...updated[idx], [field]: value };
      setEditRoutes(updated);
    }
  };

  // Render routes editor
  const renderRoutesEditor = (routeList: ContractRoute[], target: 'create' | 'edit') => (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <label className="block text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Routes *</label>
        <button onClick={() => addRoute(target)} className="flex items-center gap-1 text-xs text-[var(--ink-0)] hover:text-[var(--ink-0)]">
          <Plus size={12} /> Add Route
        </button>
      </div>
      <div className="space-y-2 rounded-lg border p-3 max-h-[300px] overflow-y-auto" style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-surface)' }}>
        {routeList.map((route, idx) => (
          <div key={idx} className="rounded-lg border p-3 space-y-2" style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-surface)' }}>
            <div className="flex items-center gap-2">
              <input type="text" value={route.origin} onChange={(e) => updateRoute(idx, 'origin', e.target.value, target)}
                placeholder="Origin (e.g. Mumbai Port)"
                className="flex-1 rounded-lg border px-3 py-1.5 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-surface)', color: 'var(--text-primary)' }} />
              <input type="text" value={route.destination} onChange={(e) => updateRoute(idx, 'destination', e.target.value, target)}
                placeholder="Destination (e.g. Panipat)"
                className="flex-1 rounded-lg border px-3 py-1.5 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-surface)', color: 'var(--text-primary)' }} />
              {routeList.length > 1 && (
                <button onClick={() => removeRoute(idx, target)} className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"><X size={14} /></button>
              )}
            </div>
            <div className="grid grid-cols-4 gap-2">
              <select value={route.vehicleType} onChange={(e) => updateRoute(idx, 'vehicleType', e.target.value, target)}
                className="rounded-lg border bg-white px-2 py-1.5 text-xs focus:border-[var(--ink-0)] focus:outline-none"
                style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-surface)', color: 'var(--text-primary)' }}>
                {VEHICLE_TYPES.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
              <input type="number" value={route.maxTonnage || ''} onChange={(e) => updateRoute(idx, 'maxTonnage', e.target.value, target)}
                placeholder="Max Tonnage" min="0" step="0.01"
                className="rounded-lg border px-2 py-1.5 text-xs focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-surface)', color: 'var(--text-primary)' }} />
              <input type="number" value={route.ratePerTrip || ''} onChange={(e) => updateRoute(idx, 'ratePerTrip', e.target.value, target)}
                placeholder="Rate/Trip" min="0" step="0.01"
                className="rounded-lg border px-2 py-1.5 text-xs focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-surface)', color: 'var(--text-primary)' }} />
              <input type="number" value={route.ratePerKg || ''} onChange={(e) => updateRoute(idx, 'ratePerKg', e.target.value, target)}
                placeholder="Rate/Kg" min="0" step="0.01"
                className="rounded-lg border px-2 py-1.5 text-xs focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-surface)', color: 'var(--text-primary)' }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // Render form fields (shared between create & edit)
  const renderFormFields = (
    formData: typeof form | typeof editForm,
    setFormData: (data: any) => void,
  ) => (
    <>
      <div>
        <label htmlFor="tc-partner" className="mb-1 block text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Partner / Vendor *</label>
        {/* A user pick fills that partner's saved terms and replaces the old ones; Edit never does this on open. */}
        <select id="tc-partner" value={formData.vendorId} onChange={(e) => setFormData({ ...formData, vendorId: e.target.value, paymentTerms: vendors.find(v => v.id === e.target.value)?.paymentTerms || '' })}
          className="w-full rounded-lg border px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
          style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-surface)', color: 'var(--text-primary)' }}>
          <option value="">Select Partner / Vendor</option>
          {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
        </select>
      </div>
      <div>
        <label className="mb-1 block text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Title *</label>
        <input type="text" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          placeholder="e.g. Annual Transport Agreement - Delhi NCR Routes"
          className="w-full rounded-lg border px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
          style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-surface)', color: 'var(--text-primary)' }} />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="mb-1 block text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Valid From *</label>
          <input type="date" value={formData.validFrom} onChange={(e) => setFormData({ ...formData, validFrom: e.target.value })}
            className="w-full rounded-lg border px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
            style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-surface)', color: 'var(--text-primary)' }} />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Valid To *</label>
          <input type="date" value={formData.validTo} onChange={(e) => setFormData({ ...formData, validTo: e.target.value })}
            className="w-full rounded-lg border px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
            style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-surface)', color: 'var(--text-primary)' }} />
        </div>
      </div>
      <div>
        <label className="mb-1 block text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Payment Terms</label>
        <select aria-label="Payment Terms" value={formData.paymentTerms} onChange={(e) => setFormData({ ...formData, paymentTerms: e.target.value })}
          className="w-full rounded-lg border px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
          style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-surface)', color: 'var(--text-primary)' }}>
          <option value="">Not set</option>
          {termOptions(formData.paymentTerms).map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>
      <div>
        <label className="mb-1 block text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Notes</label>
        <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          placeholder="Additional terms, conditions, or remarks..."
          rows={3}
          className="w-full rounded-lg border px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
          style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-surface)', color: 'var(--text-primary)' }} />
      </div>
    </>
  );

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>Transport Contracts</h1>
          <p className="text-sm" style={{ color: 'var(--text-secondary, #6b7280)' }}>Manage transport partner agreements, routes and rates</p>
        </div>
        <button
          onClick={() => { setRoutes([{ origin: '', destination: '', vehicleType: 'Open Truck', maxTonnage: undefined, ratePerTrip: undefined, ratePerKg: undefined }]); setShowModal(true); }}
          className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] transition-colors"
        >
          <Plus size={16} />
          Create Contract
        </button>
      </div>

      {/* Search & Filters */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input type="text" placeholder="Search contracts..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="w-full rounded-lg border py-2 pl-9 pr-4 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
            style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-surface)', color: 'var(--text-primary)' }} />
        </div>
        <button onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors ${showFilters ? 'border-[var(--ink-0)] bg-[var(--saffron-50)] text-[var(--ink-0)]' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'}`}>
          <Filter size={16} /> Filters
        </button>
      </div>

      {showFilters && (
        <div className="mb-4 flex flex-wrap gap-3 rounded-lg border p-4" style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-surface)' }}>
          <select value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}
            className="rounded-lg border px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
            style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-surface)', color: 'var(--text-primary)' }}>
            <option value="">All Statuses</option>
            {Object.keys(STATUS_LABELS).map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
          </select>
          {filterStatus && (
            <button onClick={() => setFilterStatus('')} className="flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50">
              <X size={14} /> Clear
            </button>
          )}
        </div>
      )}

      {/* Table */}
      <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-surface)' }}>
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
            <span className="ml-2 text-sm text-gray-500">Loading contracts...</span>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-20 text-red-500">
            <p className="text-sm">{error}</p>
            <button onClick={fetchContracts} className="mt-2 text-sm text-[var(--ink-0)] hover:underline">Retry</button>
          </div>
        ) : contracts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400">
            <FileText size={40} className="mb-2" />
            <p className="text-sm">No transport contracts found</p>
            <p className="text-xs">Create a contract to define transport partner agreements</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs font-medium uppercase tracking-wider" style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-surface)', color: 'var(--text-secondary, #6b7280)' }}>
                  <th className="cursor-pointer px-4 py-3" onClick={() => handleSort('number')}>
                    <span className="flex items-center gap-1">Number <SortIcon field="number" /></span>
                  </th>
                  <th className="px-4 py-3">Partner / Vendor</th>
                  <th className="px-4 py-3">Title</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="cursor-pointer px-4 py-3" onClick={() => handleSort('validFrom')}>
                    <span className="flex items-center gap-1">Valid From <SortIcon field="validFrom" /></span>
                  </th>
                  <th className="cursor-pointer px-4 py-3" onClick={() => handleSort('validTo')}>
                    <span className="flex items-center gap-1">Valid To <SortIcon field="validTo" /></span>
                  </th>
                  <th className="px-4 py-3 text-center">Routes</th>
                  <th className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y" style={{ borderColor: 'var(--border-primary)' }}>
                {contracts.map(c => (
                  <tr key={c.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 font-mono text-xs font-medium" style={{ color: 'var(--text-primary)' }}>{c.number}</td>
                    <td className="px-4 py-3" style={{ color: 'var(--text-primary)' }}>{c.vendorName || c.vendor?.name || '-'}</td>
                    <td className="px-4 py-3 max-w-[200px] truncate" style={{ color: 'var(--text-secondary, #6b7280)' }}>{c.title}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[c.status] || 'bg-gray-100 text-gray-600'}`}>
                        {STATUS_LABELS[c.status] || c.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs" style={{ color: 'var(--text-secondary, #6b7280)' }}>{formatDate(c.validFrom)}</td>
                    <td className="px-4 py-3 text-xs" style={{ color: 'var(--text-secondary, #6b7280)' }}>{formatDate(c.validTo)}</td>
                    <td className="px-4 py-3 text-center" style={{ color: 'var(--text-secondary, #6b7280)' }}>{c._count?.routes ?? c.routes?.length ?? 0}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        {c.status === 'draft' && (
                          <>
                            <button onClick={() => handleActivate(c)}
                              className="flex items-center gap-1 rounded-lg border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-green-50 hover:border-green-300 hover:text-green-700 transition-colors" title="Activate">
                              <CheckCircle size={12} /> Activate
                            </button>
                            <button onClick={() => openEditModal(c)} className="rounded p-1 text-gray-400 hover:bg-[var(--saffron-50)] hover:text-[var(--ink-0)]" title="Edit"><Pencil size={14} /></button>
                            <button onClick={() => handleDelete(c)} className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600" title="Delete"><Trash2 size={14} /></button>
                          </>
                        )}
                        {c.status === 'active' && (
                          <button onClick={() => handleTerminate(c)}
                            className="flex items-center gap-1 rounded-lg border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-orange-50 hover:border-orange-300 hover:text-orange-700 transition-colors" title="Terminate">
                            <XCircle size={12} /> Terminate
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-700">
          <div className="text-sm text-gray-400">
            Showing {((page - 1) * limit) + 1}–{Math.min(page * limit, total)} of {total}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              className="px-3 py-1 text-sm rounded border border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed">Previous</button>
            <span className="text-sm text-gray-400">Page {page} of {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              className="px-3 py-1 text-sm rounded border border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed">Next</button>
          </div>
        </div>
      )}

      {/* Create Contract Modal */}
      {showModal && (
        <Modal open onClose={() => setShowModal(false)} className="!max-w-2xl">
            <div className="flex items-center justify-between border-b px-6 py-4 sticky top-0 z-10 rounded-t-xl" style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-surface)' }}>
              <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Create Transport Contract</h2>
              <RequestCloseButton className="text-gray-400 hover:text-gray-600"><X size={20} /></RequestCloseButton>
            </div>
            <div className="space-y-4 px-6 py-4">
              {renderFormFields(form, setForm)}
              {renderRoutesEditor(routes, 'create')}
            </div>
            <div className="flex items-center justify-end gap-3 border-t px-6 py-4" style={{ borderColor: 'var(--border-primary)' }}>
              <RequestCloseButton className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</RequestCloseButton>
              <button onClick={handleCreate}
                disabled={saving || !form.vendorId || !form.title || !form.validFrom || !form.validTo}
                className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50">
                {saving && <Loader2 size={14} className="animate-spin" />}
                Create Contract
              </button>
            </div>
        </Modal>
      )}

      {/* Edit Contract Modal */}
      {showEditModal && (
        <Modal open onClose={() => setShowEditModal(false)} className="!max-w-2xl">
            <div className="flex items-center justify-between border-b px-6 py-4 sticky top-0 z-10 rounded-t-xl" style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-surface)' }}>
              <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Edit Transport Contract</h2>
              <RequestCloseButton className="text-gray-400 hover:text-gray-600"><X size={20} /></RequestCloseButton>
            </div>
            <div className="space-y-4 px-6 py-4">
              {renderFormFields(editForm, setEditForm)}
              {renderRoutesEditor(editRoutes, 'edit')}
            </div>
            <div className="flex items-center justify-end gap-3 border-t px-6 py-4" style={{ borderColor: 'var(--border-primary)' }}>
              <RequestCloseButton className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</RequestCloseButton>
              <button onClick={handleEdit}
                disabled={editSaving || !editForm.vendorId || !editForm.title || !editForm.validFrom || !editForm.validTo}
                className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50">
                {editSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                Save Changes
              </button>
            </div>
        </Modal>
      )}
    </div>
  );
}

function RequestCloseButton({ children, ...props }: ButtonHTMLAttributes<HTMLButtonElement>) {
  const { requestClose } = useModalClose();
  return <button type="button" {...props} onClick={requestClose}>{children}</button>;
}
